import { gql, TypedDocumentNode } from '@apollo/client';
import { CartPrice, CART_PRICE_FRAGMENT } from '../useCart';
import { ADDRESS_ATTRIBUTES_FRAGMENT, RestAddressAttributes } from '../useAddress';
import { PAYMENT_METHOD_ATTRIBUTES_FRAGMENT, PaymentMethodAttributes } from '../usePaymentMethods';
import { OrderItem } from '../useOrder';
import { ORDER_ITEM_FRAGMENT } from '../useOrder';
import { OrderState } from '../useUpdateOrder';

export type OrderSummaryTransaction = {
  __typename: 'RestOrderSummaryTransaction';
  amount: string;
  details: string;
  source: string;
};

export type TaxAndShippingChanges = {
  __typename: 'RestTaxAndShippingChanges';
  message: string;
  orderTotalChange: string;
};

type ItemRelationship = {
  data: {
    id: string;
  }[];
};

export type OrderSummaryRelationships = {
  openItems: ItemRelationship;
  shippedItems: ItemRelationship;
  cancelledItems: ItemRelationship;
  returnItems: ItemRelationship;
  returnRequestedItems: ItemRelationship;
  returnReceivedItems: ItemRelationship;
  returnRejectedItems: ItemRelationship;
  refundedItems: ItemRelationship;
};

export type OrderOpenItem = {
  __typeName: 'RestOrderSummaryOpenItem';
  id: number;
};

export type OrderSummary = {
  __typename: 'RestOrderSummary';
  id: string;
  attributes: {
    __typename: 'RestOrderSummaryAttributes';
    orderNumber: string;
    affirmSecondChargeEnabled: boolean | null;
    address: RestAddressAttributes;
    currency: string;
    eligibleForRewards: boolean;
    firstOrder: boolean;
    message: string;
    isAddressChangeable: boolean;
    isPaymentChangeable: boolean;
    isAmazonSupported: boolean;
    isPayPalSupported: boolean;
    paypalClientToken: string;
    selectedAddressId: number | string;
    userIsMember: boolean;
    guestAccessToken: string;
    userEmail: string;
    taxAndShippingChanges: TaxAndShippingChanges;
    giftNote: string | null;
    paymentMethod: PaymentMethodAttributes;
    orderState: OrderState;
    selectedItemsIds: number[] | null;
    selectedPaymentId: number;
    hasPendingAuthorizationItems: boolean;
    errors: string[] | null;
    restrictedAddressIds: number[] | null;
    paymentDetail: {
      __typename: 'RestOrderSummaryPaymentDetail';
      card: CartPrice | null;
      credit: CartPrice | null;
      rewards: CartPrice | null;
    };
    paymentSummary: {
      __typename: 'RestOrderSummaryPaymentSummary';
      amountToBeCharged: CartPrice;
      costItemGiftCopy: boolean;
      discounts: CartPrice;
      duty: CartPrice | null;
      dutyLabel: string | null;
      giftCode: string | null;
      payToday: CartPrice;
      pendingPoints: number;
      orderBalance: string;
      remainingBalance: CartPrice;
      rewardPointsEarned: string;
      rewardPointsRedeemed: string;
      rewardsBalance: CartPrice;
      rewardsToBeUsed: CartPrice;
      siteCreditBalance: CartPrice;
      siteCreditToBeUsed: CartPrice;
      rewards: string | null;
      salesTax: CartPrice | null;
      salesTaxLabel: string | null;
      salesTaxesAndDuties: CartPrice | null;
      shippingAndPackaging: CartPrice;
      siteCredit: string | null;
      subtotal: CartPrice;
      total: CartPrice;
      totalPaid: CartPrice;
      totalAmountPaid: CartPrice;
      vat: CartPrice | null;
      vatLabel: string | null;
      gst: CartPrice | null;
      gstLabel: string | null;
    };
    rewardsToBeEarned: {
      __typename: 'RestOrderSummaryRewardsToBeEarned';
      totalPoints: number;
    };
    rewardPointsAvailable: string;
    shippingMethod: string;
    shippingMethodId: number;
    sortedOpenItems: {
      __typename: 'RestOrderSummarySortedOpenItems';
      paidInFullItems: OrderOpenItem[];
      onOrderItems: OrderOpenItem[];
    };
    transactions: OrderSummaryTransaction[];
    useMoRewards: boolean;
    useSiteCredit: boolean;
  };
  relationships: OrderSummaryRelationships | null;
};

export type GetOrderSummaryQueryVariables = {
  countryCode: string;
  orderNumber: string;
};

export type GetOrderSummaryQuery = {
  getOrderSummary: {
    __typename: 'RestOrderSummaryPayload';
    data: OrderSummary[];
    included: OrderItem[];
  };
};

const REST_ORDER_SUMMARY_RELATIONSHIPS_FRAGMENT = gql`
  fragment RestOrderSummaryRelationshipsFragment on RestOrderSummaryRelationships {
    openItems: open_items @type(name: "RestOrderItemRelationship")
    shippedItems: shipped_items @type(name: "RestOrderItemRelationship")
    cancelledItems: cancelled_items @type(name: "RestOrderItemRelationship")
    returnItems: return_items @type(name: "RestOrderItemRelationship")
    returnRequestedItems: return_requested_items @type(name: "RestOrderItemRelationship")
    returnReceivedItems: return_recieved_items @type(name: "RestOrderItemRelationship")
    refundedItems: refunded_items @type(name: "RestOrderItemRelationship")
    returnRejectedItems: return_rejected_items @type(name: "RestOrderItemRelationship")
  }
`;

export const ORDER_SUMMARY_FRAGMENT = gql`
  fragment OrderSummaryFragment on RestOrderSummary {
    id
    attributes @type(name: "RestOrderSummaryAttributes") {
      hasPendingAuthorizationItems: has_pending_authorization_items
      orderNumber: order_number
      affirmSecondChargeEnabled: affirm_second_charge_enabled
      address @type(name: "RestAddressAttributes") {
        ...AddressAttributesFragment
      }
      selectedAddressId: selected_address_id
      message
      isAddressChangeable: is_address_changeable
      isPaymentChangeable: is_payment_changeable
      isAmazonSupported: is_amazon_supported
      isPayPalSupported: is_paypal_supported
      paypalClientToken: paypal_client_token
      taxAndShippingChanges: tax_and_shipping_changes @type(name: RestTaxAndShippingChanges) {
        message
        orderTotalChange: order_total_change
      }
      currency: currency_unit
      eligibleForRewards: eligible_for_rewards
      firstOrder: first_order
      userIsMember: user_is_member
      userEmail: user_email
      giftNote: gift_note
      orderState: order_state
      selectedPaymentId: selected_payment_id
      guestAccessToken: guest_access_token
      hasPendingAuthorizationItems: has_pending_authorization_items
      errors
      restrictedAddressIds: restricted_addresses_ids
      paymentMethod: order_payment_method @type(name: "RestPaymentMethodAttributes") {
        ...PaymentMethodAttributesFragment
      }
      paymentDetail: payment_detail @type(name: "RestOrderSummaryPaymentDetail") {
        card @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        credit @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        rewards @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
      }
      paymentSummary: payment_summary @type(name: "RestOrderSummaryPaymentSummary") {
        amountToBeCharged: amount_to_be_charged @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        costItemGiftCopy: cost_item_gift_copy
        discounts @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        pendingPoints: pending_points
        duty @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        gst @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        rewardsToBeUsed: rewards_to_be_used @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        rewardsBalance: rewards_balance @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        siteCreditBalance: site_credit_balance @type(name: RestCartPrice) {
          ...CartPriceFragment
        }
        siteCreditToBeUsed: site_credit_to_be_used @type(name: RestCartPrice) {
          ...CartPriceFragment
        }
        totalAmountPaid: total_amount_paid
        dutyLabel: duty_label
        orderBalance: order_balance
        gstLabel: gst_label
        giftCode: gift_code
        payToday: pay_today @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        remainingBalance: remaining_balance @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        rewardPointsEarned: total_reward_points_earned_with_delimeter
        rewardPointsRedeemed: total_rewards_points_redeemed
        rewards
        salesTax: sales_tax @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        salesTaxLabel: sales_tax_label
        salesTaxesAndDuties: sales_taxes_and_duties @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        shippingAndPackaging: shipping_and_packaging @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        siteCredit: site_credit
        subtotal: order_subtotal @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        total: order_total @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        totalPaid: total_amount_paid @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        vat @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        vatLabel: vat_label
        rewardPointsEarned: total_reward_points_earned_with_delimeter
      }
      rewardsToBeEarned: rewards_to_be_earned @type(name: "RestOrderSummaryRewardsToBeEarned") {
        totalPoints: total_points
      }
      rewardPointsAvailable: user_total_reward_points
      shippingMethod: shipping_method
      shippingMethodId: shipment_method
      sortedOpenItems: sorted_open_items @type(name: "RestOrderSummarySortedOpenItems") {
        paidInFullItems: paid_in_full_items @type(name: "RestOrderSummaryOpenItem") {
          id
        }
        onOrderItems: on_order_items @type(name: "RestOrderSummaryOpenItem") {
          id
        }
      }
      transactions: payment_transactions_view @type(name: "RestOrderSummaryTransaction") {
        amount
        details: tn_detail
        source
      }
      selectedItemsIds: selected_items_ids
      useMoRewards: use_mo_rewards
      useSiteCredit: use_site_credit
    }
    relationships @type(name: "RestOrderSummaryRelationships") {
      ...RestOrderSummaryRelationshipsFragment
    }
  }
  ${ADDRESS_ATTRIBUTES_FRAGMENT}
  ${PAYMENT_METHOD_ATTRIBUTES_FRAGMENT}
  ${CART_PRICE_FRAGMENT}
  ${REST_ORDER_SUMMARY_RELATIONSHIPS_FRAGMENT}
`;

export const GET_ORDER_SUMMARY_QUERY: TypedDocumentNode<
  GetOrderSummaryQuery,
  GetOrderSummaryQueryVariables
> = gql`
  query GetOrderSummaryQuery($orderNumber: string!, $countryCode: String!) {
    getOrderSummary(orderNumber: $orderNumber, countryCode: $countryCode)
      @rest(
        type: "RestOrderSummaryPayload"
        endpoint: "cart"
        path: "/v3.5/order_summary?order_number={args.orderNumber}&country_code={args.countryCode}"
      ) {
      data @type(name: "RestOrderSummary") {
        ...OrderSummaryFragment
      }
      included @type(name: "RestOrderItem") {
        ...OrderItemFragment
      }
    }
  }
  ${ORDER_SUMMARY_FRAGMENT}
  ${ORDER_ITEM_FRAGMENT}
`;
